import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Card,
  Group,
  Loader,
  Pagination,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconActivity,
  IconAlertCircle,
  IconAlertTriangle,
  IconCheck,
  IconLoader2,
  IconRefresh,
  IconExternalLink,
  IconX,
} from "@tabler/icons-react";
import { EmptyState } from "../../components/EmptyState";

// ---------------------------------------------------------------------------
// Types (matches /api/admin/scrapers contract)
// ---------------------------------------------------------------------------

interface RetailerHealthSummary {
  retailerSlug: string;
  lastRunAt: string | null;
  lastRunStatus: string | null;
  lastCoveragePct: number | null;
  runs24H: number;
  successRate24H: number | null;
  avgDurationS24H: number | null;
}

interface ScraperRunItem {
  id: string;
  retailerSlug: string;
  status: string;
  startedAt: string;
  completedAt: string | null;
  durationS: number | null;
  inputCount: number;
  normalizedCount: number;
  errorCount: number;
  filtered3PCount: number;
  coveragePct: number | null;
  hasPayload: boolean;
}

interface ScraperRunListResponse {
  items: ScraperRunItem[];
  total: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const API_BASE = "/api/admin/scrapers";
const PAGE_SIZE = 20;
const POLL_INTERVAL_MS = 60_000;
// Per-tier health thresholds calibrated for daily scrape cadence.
// 28h degraded = 4h grace after expected 24h cycle; 36h down = missed full cycle.
// Coverage varies by retailer type (Costco member-session, DTC smaller catalogs).
const HEALTH_THRESHOLDS: Record<string, {
  staleHoursDegraded: number; staleHoursDown: number;
  coveragePctDegraded: number; coveragePctDown: number;
}> = {
  default:  { staleHoursDegraded: 28, staleHoursDown: 36, coveragePctDegraded: 80, coveragePctDown: 50 },
  costco:   { staleHoursDegraded: 28, staleHoursDown: 36, coveragePctDegraded: 60, coveragePctDown: 30 },
  samsung:  { staleHoursDegraded: 28, staleHoursDown: 36, coveragePctDegraded: 70, coveragePctDown: 40 },
  lg:       { staleHoursDegraded: 28, staleHoursDown: 36, coveragePctDegraded: 70, coveragePctDown: 40 },
};

function thresholdsFor(slug: string) {
  return HEALTH_THRESHOLDS[slug] ?? HEALTH_THRESHOLDS.default;
}

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "completed", label: "Completed" },
  { value: "partial", label: "Partial" },
  { value: "failed", label: "Failed" },
  { value: "running", label: "Running" },
];

const STATUS_COLORS: Record<string, string> = {
  completed: "green",
  partial: "yellow",
  failed: "red",
  running: "blue",
};

const STATUS_ICONS: Record<string, typeof IconCheck> = {
  completed: IconCheck,
  partial: IconAlertTriangle,
  failed: IconX,
  running: IconLoader2,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const RETAILER_NAMES: Record<string, string> = {
  amazon: "Amazon",
  best_buy: "Best Buy",
  walmart: "Walmart",
  target: "Target",
  costco: "Costco",
  samsung: "Samsung",
  lg: "LG",
};

function displayName(slug: string): string {
  return RETAILER_NAMES[slug] ?? slug.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function relativeTime(iso: string): string {
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return "Unknown";
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
}

function formatPct(value: number | null): string {
  if (value === null) return "—";
  return `${Math.round(value)}%`;
}

type HealthLevel = "healthy" | "degraded" | "down" | "no-data";

function getHealthLevel(h: RetailerHealthSummary): HealthLevel {
  if (h.lastRunAt === null) return "no-data";
  const t = thresholdsFor(h.retailerSlug);
  const hoursSinceRun =
    (Date.now() - new Date(h.lastRunAt).getTime()) / 3_600_000;
  const cov = h.lastCoveragePct;
  // Null coverage = not yet computed; only evaluate thresholds when reported
  if (hoursSinceRun > t.staleHoursDown || (cov !== null && cov < t.coveragePctDown)) return "down";
  if (hoursSinceRun > t.staleHoursDegraded || (cov !== null && cov < t.coveragePctDegraded)) return "degraded";
  return "healthy";
}

const HEALTH_BADGE: Record<HealthLevel, { color: string; label: string }> = {
  healthy: { color: "green", label: "Healthy" },
  degraded: { color: "yellow", label: "Degraded" },
  down: { color: "red", label: "Down" },
  "no-data": { color: "gray", label: "No Data" },
};

function coverageColor(pct: number | null, slug = "default"): string {
  if (pct === null) return "gray";
  const t = thresholdsFor(slug);
  if (pct >= t.coveragePctDegraded) return "green";
  if (pct >= t.coveragePctDown) return "yellow";
  return "red";
}

// ---------------------------------------------------------------------------
// Data hooks
// ---------------------------------------------------------------------------

function useHealthSummary() {
  const [data, setData] = useState<RetailerHealthSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchHealth = useCallback(async (signal?: AbortSignal) => {
    try {
      const res = await fetch(`${API_BASE}/runs/health-summary`, { signal });
      if (!res.ok) { setError(`Request failed (${res.status})`); return; }
      const json: RetailerHealthSummary[] = await res.json();
      setError(null);
      setData(json);
      setLastUpdated(new Date());
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") return;
      setError("Network error — retrying");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void fetchHealth(controller.signal);
    const id = setInterval(() => void fetchHealth(controller.signal), POLL_INTERVAL_MS);
    return () => { controller.abort(); clearInterval(id); };
  }, [fetchHealth]);

  return { data, loading, error, lastUpdated, refetch: fetchHealth };
}

function useRunList(retailerFilter: string, statusFilter: string, page: number) {
  const [data, setData] = useState<ScraperRunListResponse>({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRuns = useCallback(async (signal?: AbortSignal) => {
    const params = new URLSearchParams();
    if (retailerFilter) params.set("retailer", retailerFilter);
    if (statusFilter) params.set("status", statusFilter);
    params.set("limit", String(PAGE_SIZE));
    params.set("offset", String((page - 1) * PAGE_SIZE));
    try {
      const res = await fetch(`${API_BASE}/runs?${params.toString()}`, { signal });
      if (!res.ok) { setError(`Request failed (${res.status})`); return; }
      const json: ScraperRunListResponse = await res.json();
      setError(null);
      setData(json);
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") return;
      setError("Network error — retrying");
    } finally {
      setLoading(false);
    }
  }, [retailerFilter, statusFilter, page]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    void fetchRuns(controller.signal);
    const id = setInterval(() => void fetchRuns(controller.signal), POLL_INTERVAL_MS);
    return () => { controller.abort(); clearInterval(id); };
  }, [fetchRuns]);

  return { data, loading, error, refetch: fetchRuns };
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function HealthCard({ summary }: { summary: RetailerHealthSummary }) {
  const level = getHealthLevel(summary);
  const badge = HEALTH_BADGE[level];

  return (
    <Card shadow="xs" padding="md" radius="md" withBorder>
      <Group justify="space-between" mb="xs">
        <Text fw={600}>{displayName(summary.retailerSlug)}</Text>
        <Badge color={badge.color} variant="light" size="sm">
          {badge.label}
        </Badge>
      </Group>
      <Stack gap={4}>
        <Group justify="space-between">
          <Text size="sm" c="dimmed">Last run</Text>
          <Text size="sm">
            {summary.lastRunAt ? (
              <Tooltip label={new Date(summary.lastRunAt).toLocaleString()}>
                <span>{relativeTime(summary.lastRunAt)}</span>
              </Tooltip>
            ) : "Never"}
          </Text>
        </Group>
        <Group justify="space-between">
          <Text size="sm" c="dimmed">Coverage</Text>
          <Text size="sm" c={coverageColor(summary.lastCoveragePct, summary.retailerSlug)}>
            {formatPct(summary.lastCoveragePct)}
          </Text>
        </Group>
        <Group justify="space-between">
          <Text size="sm" c="dimmed">24h success</Text>
          <Text size="sm">{formatPct(summary.successRate24H)}</Text>
        </Group>
        <Group justify="space-between">
          <Text size="sm" c="dimmed">24h runs</Text>
          <Text size="sm">{summary.runs24H}</Text>
        </Group>
      </Stack>
    </Card>
  );
}

function StatusBadge({ status }: { status: string }) {
  const StatusIcon = STATUS_ICONS[status];
  return (
    <Badge
      color={STATUS_COLORS[status] ?? "gray"}
      variant="light"
      size="sm"
      leftSection={StatusIcon ? <StatusIcon size={12} /> : undefined}
    >
      {status}
    </Badge>
  );
}

function showPayloadError(): void {
  notifications.show({
    color: "red",
    title: "Payload unavailable",
    message: "Could not load payload URL. Try again.",
  });
}

function RunHistoryTable({
  runs,
  loading,
}: {
  runs: ScraperRunItem[];
  loading: boolean;
}) {
  const handleViewPayload = async (runId: string) => {
    try {
      const res = await fetch(`${API_BASE}/runs/${encodeURIComponent(runId)}/payload-url`);
      if (!res.ok) { showPayloadError(); return; }
      const json: { url: string } = await res.json();
      if (!json.url.startsWith("https://")) { showPayloadError(); return; }
      window.open(json.url, "_blank", "noopener,noreferrer");
    } catch {
      showPayloadError();
    }
  };

  if (loading && runs.length === 0) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  if (runs.length === 0) {
    return (
      <EmptyState
        icon={IconActivity}
        title="No runs found"
        description="Adjust filters or wait for the next scrape cycle."
      />
    );
  }

  return (
    <Table.ScrollContainer minWidth={700}>
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Retailer</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Started</Table.Th>
            <Table.Th>Duration</Table.Th>
            <Table.Th ta="right">Inputs</Table.Th>
            <Table.Th ta="right">OK</Table.Th>
            <Table.Th ta="right">Errors</Table.Th>
            <Table.Th ta="right">Coverage</Table.Th>
            <Table.Th>Payload</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {runs.map((run) => (
            <Table.Tr key={run.id}>
              <Table.Td>{displayName(run.retailerSlug)}</Table.Td>
              <Table.Td>
                <StatusBadge status={run.status} />
              </Table.Td>
              <Table.Td>
                <Tooltip label={new Date(run.startedAt).toLocaleString()}>
                  <span>{relativeTime(run.startedAt)}</span>
                </Tooltip>
              </Table.Td>
              <Table.Td>{formatDuration(run.durationS)}</Table.Td>
              <Table.Td ta="right">{run.inputCount}</Table.Td>
              <Table.Td ta="right">{run.normalizedCount}</Table.Td>
              <Table.Td ta="right">
                <Text c={run.errorCount > 0 ? "red" : undefined} size="sm" component="span">
                  {run.errorCount > 0 && <IconAlertTriangle size={14} aria-hidden style={{ verticalAlign: "text-bottom", marginRight: 2 }} />}
                  {run.errorCount}
                </Text>
              </Table.Td>
              <Table.Td ta="right">
                <Text c={coverageColor(run.coveragePct, run.retailerSlug)} size="sm" component="span">
                  {formatPct(run.coveragePct)}
                </Text>
              </Table.Td>
              <Table.Td>
                <Tooltip label={run.hasPayload ? "View raw payload" : "No payload"}>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    disabled={!run.hasPayload}
                    onClick={() => { void handleViewPayload(run.id); }}
                    aria-label={run.hasPayload ? "View raw payload" : "No payload available"}
                  >
                    <IconExternalLink size={16} />
                  </ActionIcon>
                </Tooltip>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function ScrapersPage() {
  const { data: healthData, loading: healthLoading, error: healthError, lastUpdated, refetch: refetchHealth } =
    useHealthSummary();
  const [retailerFilter, setRetailerFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);

  const { data: runData, loading: runsLoading, error: runsError, refetch: refetchRuns } =
    useRunList(retailerFilter, statusFilter, page);

  const isRefreshing = healthLoading || runsLoading;

  const handleRetailerChange = (v: string | null) => {
    setRetailerFilter(v ?? "");
    setPage(1);
  };

  const handleStatusChange = (v: string | null) => {
    setStatusFilter(v ?? "");
    setPage(1);
  };

  const retailerOptions = useMemo(() => {
    const slugs = healthData.map((h) => h.retailerSlug);
    return [
      { value: "", label: "All retailers" },
      ...slugs.map((s) => ({ value: s, label: displayName(s) })),
    ];
  }, [healthData]);

  const handleRefresh = () => {
    void refetchHealth();
    void refetchRuns();
  };

  const totalPages = Math.max(1, Math.ceil(runData.total / PAGE_SIZE));

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Title order={2}>Scrapers</Title>
        <Group gap="sm">
          {lastUpdated && (
            <Text size="xs" c="dimmed">
              Updated {relativeTime(lastUpdated.toISOString())}
            </Text>
          )}
          <ActionIcon variant="light" onClick={handleRefresh} loading={isRefreshing} aria-label="Refresh">
            <IconRefresh size={18} />
          </ActionIcon>
        </Group>
      </Group>

      {/* Health cards */}
      {healthError && (
        <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
          {healthError}
        </Alert>
      )}
      {healthLoading && healthData.length === 0 && (
        <Group justify="center" py="xl">
          <Loader size="sm" />
        </Group>
      )}
      {!healthLoading && healthData.length === 0 && (
        <EmptyState
          icon={IconActivity}
          title="No scrape runs yet"
          description="Runs will appear here once the pricing cycle starts."
        />
      )}
      {healthData.length > 0 && (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3, xl: 4 }}>
          {healthData.map((h) => (
            <HealthCard key={h.retailerSlug} summary={h} />
          ))}
        </SimpleGrid>
      )}

      {/* Run history */}
      <Stack gap="sm">
        <Group justify="space-between">
          <Title order={4}>Run History</Title>
          <Group gap="sm">
            <Select
              size="xs"
              w={160}
              data={retailerOptions}
              value={retailerFilter}
              onChange={handleRetailerChange}
              aria-label="Filter by retailer"
            />
            <Select
              size="xs"
              w={160}
              data={STATUS_OPTIONS}
              value={statusFilter}
              onChange={handleStatusChange}
              aria-label="Filter by status"
            />
          </Group>
        </Group>

        {runsError && (
          <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
            {runsError}
          </Alert>
        )}
        <RunHistoryTable runs={runData.items} loading={runsLoading} />

        {runData.total > PAGE_SIZE && (
          <Group justify="center">
            <Pagination total={totalPages} value={page} onChange={setPage} size="sm" />
          </Group>
        )}
      </Stack>
    </Stack>
  );
}
